import { create } from 'zustand';
import { Topic, SearchState, FAQ } from './types';
import { supabase } from './lib/supabase';

const initialTopics: Topic[] = [
  {
    id: '1',
    question: 'לקנות רכב',
    bestTime: 'נובמבר-דצמבר',
    explanation: 'סוף שנה – מבצעי יבואנים',
    tip: 'בדוק רכב 0 ק"מ לתצוגה'
  },
  {
    id: '2',
    question: 'לטוס ליוון',
    bestTime: 'מאי-יוני',
    explanation: 'לפני החום וההמונים',
    tip: 'מחירים סבירים וטיסות קלות'
  },
  {
    id: '3',
    question: 'לשתול בזיליקום',
    bestTime: 'אפריל',
    explanation: 'מזג אוויר מושלם לנביטה',
    tip: 'השקיה יומית ועדינה'
  },
  {
    id: '4',
    question: 'לקנות פלאפון',
    bestTime: 'נובמבר',
    explanation: 'בלאק פריידי והנחות סוף שנה',
    tip: 'דגמים חדשים יוצאים בספטמבר'
  }
];

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: string;
  published_at: string;
  is_published: boolean;
  image_url?: string;
}

interface BlogState {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  fetchPosts: () => Promise<void>;
  createPost: (post: Omit<BlogPost, 'id'>) => Promise<BlogPost | null>;
  updatePost: (id: string, post: Partial<BlogPost>) => Promise<BlogPost | null>;
  deletePost: (id: string) => Promise<boolean>;
}

export const useBlogStore = create<BlogState>((set) => ({
  posts: [],
  loading: false,
  error: null,
  fetchPosts: async () => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      set({ posts: data || [], loading: false });
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      set({ error: 'Failed to fetch blog posts', loading: false });
    }
  },
  createPost: async (post) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert([post])
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        posts: [data, ...state.posts]
      }));

      return data;
    } catch (error) {
      console.error('Error creating blog post:', error);
      set({ error: 'Failed to create blog post' });
      return null;
    }
  },
  updatePost: async (id, post) => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .update(post)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        posts: state.posts.map((p) => (p.id === id ? data : p))
      }));

      return data;
    } catch (error) {
      console.error('Error updating blog post:', error);
      set({ error: 'Failed to update blog post' });
      return null;
    }
  },
  deletePost: async (id) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        posts: state.posts.filter((p) => p.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting blog post:', error);
      set({ error: 'Failed to delete blog post' });
      return false;
    }
  }
}));

export const useStore = create<SearchState>((set) => ({
  query: '',
  setQuery: (query: string) => set({ query }),
  topics: initialTopics,
  loading: false,
  setLoading: (loading: boolean) => set({ loading }),
  aiResponse: null,
  setAiResponse: (response: Topic | null) => set({ aiResponse: response }),
  faqs: [],
  setFaqs: (faqs: FAQ[]) => set({ faqs }),
  isFaqModalOpen: false,
  setIsFaqModalOpen: (isOpen: boolean) => set({ isFaqModalOpen: isOpen }),
  saveFAQ: async (topic: Topic) => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .insert([{
          question: topic.question,
          best_time: topic.bestTime,
          explanation: topic.explanation,
          tip: topic.tip,
          is_featured: false
        }])
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        faqs: [...state.faqs, data as FAQ]
      }));

      return true;
    } catch (error) {
      console.error('Error saving FAQ:', error);
      return false;
    }
  }
}));